import React, { useState, useCallback, useMemo } from "react";
import {
  Lbl,
  Spacer,
} from "../../../../components";
import { FC } from "react";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { ProjectLaborProductMeasurement } from "../../../../redux/types/Material";
import { CheckCircle, RadioButtonUnchecked, ArrowForward } from "@mui/icons-material";
import { ContractorShop, isContractorSelected } from "./ContractorShop";

interface ContractorSelectionProps {
  handleRefetchProjectDetails: Function;
  projectData?: CustomerProjectDetails;
}

export const ContractorSelection: FC<ContractorSelectionProps> = ({
  handleRefetchProjectDetails,
  projectData,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<ProjectLaborProductMeasurement | null>(null);

  // Get labor measurements from project data and filter by isLaborSelectionRequired flag
  const laborMeasurements: ProjectLaborProductMeasurement[] = useMemo(() => {
    const allLaborMeasurements = projectData?.insuranceScope?.laborMeasurements || [];
    const productSystems = projectData?.productSystem || [];

    if (!Array.isArray(allLaborMeasurements) || allLaborMeasurements.length === 0) {
      return [];
    }

    // Return only labor measurements where productSystem has isLaborSelectionRequired: true
    return allLaborMeasurements.filter((measurement) => {
      const productSystem = productSystems.find((ps: any) => ps.id === measurement.productId);
      return productSystem?.isLaborSelectionRequired !== false;
    });
  }, [projectData]);

  const handleContractorSelection = useCallback((contractor: ProjectLaborProductMeasurement) => {
    // Navigate to the contractor shop for this product
    setSelectedProduct(contractor);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  // Check if contractor is completed (uses isSelected at laborMeasurement level or checks sub-contractors)
  const isContractorCompleted = useCallback((contractor: ProjectLaborProductMeasurement) => {
    if (contractor.isSelected) return true;
    
    return contractor.measurements?.some(measurement => 
      measurement.associatedContractor?.some(assocContractor => 
        isContractorSelected(assocContractor)
      )
    ) ?? false;
  }, []);

  // If a product is selected, show the ContractorShop
  if (selectedProduct) {
    // Get the fresh product data from projectData to ensure we have the latest contractor selections
    const freshSelectedProduct = laborMeasurements.find(
      contractor => contractor.productId === selectedProduct.productId
    ) || selectedProduct;
    
    return (
      <ContractorShop
        handleRefetchProjectDetails={handleRefetchProjectDetails}
        projectData={projectData}
        selectedProduct={freshSelectedProduct}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div
      style={{
        padding: "16px",
        backgroundColor: "#f8fafc",
        minHeight: "100%",
        width: "100%",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "8px",
            flexWrap: "wrap",
          }}
        >
          <h1
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#1f2937",
              margin: 0,
            }}
          >
            Contractor Selection
          </h1>
        </div>
        <p
          style={{
            fontSize: "14px",
            color: "#6b7280",
            margin: 0,
            lineHeight: "1.5",
          }}
        >
          Please select contractors for each product in your project. These are
          based on labor measurements from your project manager.
        </p>
      </div>

      {/* Contractors List */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          width: "100%",
        }}
      >
        {Array.isArray(laborMeasurements) && laborMeasurements.length > 0 ? (
          laborMeasurements.map((contractor, index) => {
            const isCompleted = isContractorCompleted(contractor);
            return (
            <div
              key={contractor.productId || index}
              onClick={() => handleContractorSelection(contractor)}
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "16px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                flexWrap: "wrap",
                gap: "12px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flex: 1,
                }}
              >
                {/* Contractor Name */}
                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#1f2937",
                      textTransform: "capitalize",
                    }}
                  >
                    {contractor.name?.charAt(0).toUpperCase() + 
                     contractor.name?.slice(1).toLowerCase()}
                  </h3>
                  {contractor.measurements && contractor.measurements.length > 0 && (
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "12px",
                        color: "#6b7280",
                      }}
                    >
                      {contractor.measurements.length} measurement{contractor.measurements.length > 1 ? 's' : ''} available
                    </p>
                  )}
                </div>

                {/* Status Badge */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "500",
                    backgroundColor: isCompleted 
                      ? "rgba(194, 217, 255, 1)" 
                      : "#fff",
                    border: isCompleted 
                      ? "none" 
                      : "2px solid rgba(194, 217, 255, 1)",
                  }}
                >
                  {isCompleted ? (
                    <>
                      <CheckCircle
                        style={{
                          width: 16,
                          height: 16,
                          color: "#059669",
                        }}
                      />
                      <span style={{ color: "#059669" }}>Completed</span>
                    </>
                  ) : (
                    <>
                      <RadioButtonUnchecked
                        style={{
                          width: 16,
                          height: 16,
                          color: "#f59e0b",
                        }}
                      />
                      <span style={{ color: "#f59e0b" }}>In Progress</span>
                    </>
                  )}
                </div>
              </div>

              {/* Arrow Icon */}
              <ArrowForward
                style={{
                  width: 20,
                  height: 20,
                  color: "#6b7280",
                  marginLeft: "16px",
                }}
              />
            </div>
          )})
        ) : (
          <div
            style={{
              backgroundColor: "#ffffff",
              border: "2px dashed #d1d5db",
              borderRadius: "12px",
              padding: "48px 24px",
              textAlign: "center",
              color: "#6b7280",
            }}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                backgroundColor: "#f3f4f6",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px auto",
              }}
            >
              <RadioButtonUnchecked
                style={{
                  width: 32,
                  height: 32,
                  color: "#9ca3af",
                }}
              />
            </div>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              No Contractors Available
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              No contractor measurements have been added to this project yet.
              Please contact your project manager for more information.
            </p>
          </div>
        )}
      </div>

      <Spacer size={3} direction="col" />
    </div>
  );
};
